import { snakeCase } from 'lodash';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button, ButtonGroup, CircularProgress, Divider, Icon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { ConfirmDialog, useSort, useWithId } from '@sierralabs/core-ui';
import EntityNameHeader from '../common/EntityNameHeader';
import { useEntityContext } from '../EntityContext';
import EntityFieldListRow, { SortableEntityFieldListRow } from './EntityFieldListRow';
const StyledDiv = styled.div `
  .header {
    display: flex;
    align-items: center;
    padding: 12px;
  }
  .edit-button {
    margin-left: 8px;
  }
  .MuiTableContainer-root {
    height: 100%;
  }
  .header-label {
    /* margin-right: 12px;
    margin-bottom: 20px; */
  }
  .status-layout {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
  }
  .action-buttons {
    display: flex;
    margin-left: 8px;
    align-items: center;
  }
  .saving-status {
    /* margin-top: 3px; */
    /* min-height: 19px; */
    display: flex;
    justify-content: center;
    .save-label {
      margin-left: 8px;
    }
  }
`;
const EntityFieldList = ({ entity }) => {
    const { isQueued, isUpdating, trackChanges } = useEntityContext();
    const dragChangeRef = useRef({ sourceIndex: -1, targetIndex: -1 });
    const [dragItem, setDragItem] = useState();
    const columnsWithId = useWithId(entity.columns);
    const sort = useSort(columnsWithId, item => item.id);
    const sensors = useSensors(useSensor(PointerSensor));
    const [deleteIndex, setDeleteIndex] = useState(-1);
    const isDisabled = (column) => {
        var _a;
        const columnName = column.columnName || snakeCase(column.displayName);
        return (!entity.editable ||
            column.locked ||
            !!((_a = entity.relations) === null || _a === void 0 ? void 0 : _a.some(r => {
                return (r.joinColumn &&
                    typeof r.joinColumn === 'object' &&
                    r.joinColumn.name === columnName);
            })));
    };
    const clearDragChange = () => {
        dragChangeRef.current.sourceIndex = -1;
        dragChangeRef.current.targetIndex = -1;
    };
    return (React.createElement(StyledDiv, { className: 'entity-field-list' },
        React.createElement("div", { className: 'header' },
            React.createElement(EntityNameHeader, { entity: entity }),
            React.createElement("div", { className: 'status-layout' },
                React.createElement("div", { className: 'saving-status' }, (isQueued || isUpdating) && (React.createElement(React.Fragment, null,
                    React.createElement(CircularProgress, { size: 16 }),
                    React.createElement(Typography, { variant: 'caption', className: 'save-label' }, isQueued ? 'Queued...' : 'Saving...')))),
                React.createElement(ButtonGroup, { className: 'action-buttons' },
                    React.createElement(Button, { onClick: () => {
                            // const result =
                            trackChanges.undo();
                            // TOOD: fix undo refresh on column updates
                            // if (result?.delta?.path?.includes('columns')) {
                            //   console.log('setFinal', trackChanges.final);
                            //   setTimeout(() => {
                            //     trackChanges.setFinal(final => {
                            //       if (final.config?.columns) {
                            //         final.config = {
                            //           ...final.config,
                            //           columns: [...final.config.columns]
                            //         };
                            //       }
                            //       return { ...final };
                            //     });
                            //   });
                            // }
                        } },
                        React.createElement(Icon, null, "undo")),
                    React.createElement(Button, { onClick: () => trackChanges.redo() },
                        React.createElement(Icon, null, "redo"))))),
        React.createElement(Divider, null),
        React.createElement(TableContainer, null,
            React.createElement(Table, { stickyHeader: true },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, null),
                        React.createElement(TableCell, null, "Field Name"),
                        React.createElement(TableCell, null, "Data Type"),
                        React.createElement(TableCell, null, "Array"),
                        React.createElement(TableCell, null, "Comment / Description"),
                        React.createElement(TableCell, null))),
                React.createElement(DndContext, { sensors: sensors, onDragStart: event => {
                        // prevent dragging if currently dragging an item
                        if (dragItem) {
                            return;
                        }
                        const index = columnsWithId.findIndex(item => item.id === event.active.id);
                        const item = columnsWithId[index];
                        dragChangeRef.current.sourceIndex = index;
                        setDragItem(item);
                    }, onDragOver: event => {
                        const { active, over } = event;
                        if (over && active.id !== over.id) {
                            dragChangeRef.current.targetIndex = sort.findIndex(over.id);
                            sort.reorder(active.id, over.id);
                        }
                    }, onDragEnd: () => {
                        const { sourceIndex, targetIndex } = dragChangeRef.current;
                        if (sourceIndex > -1 &&
                            targetIndex > -1 &&
                            sourceIndex !== targetIndex) {
                            trackChanges.move('columns', sourceIndex, targetIndex);
                        }
                        clearDragChange();
                        setDragItem(undefined);
                    }, onDragCancel: () => {
                        clearDragChange();
                        setDragItem(undefined);
                    } },
                    React.createElement(SortableContext, { strategy: verticalListSortingStrategy, items: sort.items.map(item => item.id) },
                        React.createElement(TableBody, null,
                            sort.items.map((item, index) => (React.createElement(SortableEntityFieldListRow, { key: item.id, index: index, column: item.item, entity: entity, columnId: item.id, disabled: isDisabled(item.item), onDeleteClick: () => setDeleteIndex(index) }))),
                            React.createElement(TableRow, null,
                                React.createElement(TableCell, { colSpan: 6 },
                                    React.createElement(Button, { variant: 'outlined', color: 'primary', disabled: !entity.editable, startIcon: React.createElement(Icon, null, "add"), onClick: () => {
                                            trackChanges.insert('columns', {
                                                displayName: '',
                                                name: '',
                                                type: 'text'
                                            });
                                        } }, "Add Field"))))),
                    React.createElement(DragOverlay, { wrapperElement: 'tbody', style: { display: 'table' } }, dragItem && (React.createElement(EntityFieldListRow, { isDragging: true, entity: entity, column: dragItem.item, columnId: dragItem.id, index: sort.findIndex(dragItem.id), disabled: isDisabled(dragItem.item) })))))),
        React.createElement(ConfirmDialog, { open: deleteIndex > -1, title: 'Confirmation', message: 'Are you sure you want to delete this item?', confirmLabel: 'Confirm', onCancel: () => setDeleteIndex(-1), onConfirm: () => {
                trackChanges.delete(`columns[${deleteIndex}]`);
                setDeleteIndex(-1);
            } })));
};
export default EntityFieldList;
//# sourceMappingURL=EntityFieldList.js.map